import React from 'react';
import { Button } from '@grofers/dock-design-kit';
import { Skeleton } from '../../../ComponentsV1/Skeleton/Skeleton';
import ProductCard from './ProductCard';
import UploadedImages from './UploadedImages';
import ComplaintTypeDropdown from './ComplaintTypeDropdown';
import { MOCK_PRODUCTS, MOCK_UPLOADED_IMAGES } from '../constants';

const GUIDANCE_STATUS = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

/**
 * The complaint-type dropdown + selected-item list + Confirm action - the
 * only part that actually depends on `identify_complaint`, so it's the
 * only part that skeletons while that's polling.
 *
 * `identify_complaint`'s `options` is now two levels - each parent complaint
 * type may carry its own `sub_options` (same `{slug, display_text}` shape).
 * `ComplaintTypeDropdown` renders both levels as one grouped dropdown
 * (parent as a header, sub-options indented underneath) rather than two
 * separate dropdowns - the slug actually confirmed is the most specific one
 * picked (the sub-type's if there is one, otherwise the parent's, for a
 * leaf parent with no sub-options).
 *
 * `selectedParentSlug`/`selectedSubSlug` (and their setters) are controlled
 * by CopilotView rather than owned here - AccordionStep unmounts this
 * component while its step is collapsed, so local state would reset every
 * time the agent closed step 2 (to look at a later step) and reopened it.
 */
const ComplaintTypeSection = ({
  products,
  complaintTypeOptions,
  guidanceStatus,
  selectedParentSlug,
  onSelectedParentSlugChange,
  selectedSubSlug,
  onSelectedSubSlugChange,
  onConfirm,
}) => {
  if (guidanceStatus === GUIDANCE_STATUS.PENDING || !guidanceStatus) {
    return (
      <Skeleton
        className='tw-gap-2'
        rows={[
          { cols: 1, height: '4rem' },
          { cols: 1, height: '8em' },
        ]}
      />
    );
  }

  if (guidanceStatus === GUIDANCE_STATUS.FAILED) {
    return (
      <div className='tw-w-full tw-rounded-[12px] tw-bg-[#fef3f2] tw-p-[12px]'>
        <p className='tw-text-[12px] tw-font-regular tw-text-red-500 tw-m-0'>
          Could not identify the complaint. Please try again.
        </p>
      </div>
    );
  }

  const selectedParent = complaintTypeOptions.find(
    (option) => option.value === selectedParentSlug,
  );
  const subOptions = selectedParent?.subOptions || [];
  const finalSlug =
    subOptions.length > 0 ? selectedSubSlug : selectedParentSlug;

  const handleConfirm = () => {
    if (!finalSlug) return;
    onConfirm(finalSlug);
  };

  return (
    <>
      <div className='tw-flex tw-flex-col tw-w-full tw-bg-[#f9fafb] tw-rounded-[12px] tw-p-[12px] tw-gap-[16px]'>
        <ComplaintTypeDropdown
          options={complaintTypeOptions}
          selectedParentSlug={selectedParentSlug}
          selectedSubSlug={selectedSubSlug}
          onSelect={(parentSlug, subSlug) => {
            onSelectedParentSlugChange(parentSlug);
            onSelectedSubSlugChange(subSlug);
          }}
        />

        <div className='tw-flex tw-flex-col tw-gap-[8px] tw-w-full'>
          {products.map((product) => (
            <div
              key={product.id}
              className='tw-w-full tw-rounded-[8px] tw-overflow-hidden tw-border tw-border-solid tw-border-[#f3f4f6] tw-bg-white'
            >
              <ProductCard
                imageUrl={product.imageUrl}
                name={product.name}
                quantityLabel={product.quantityLabel}
                count={product.count}
                maxCount={product.maxCount}
                disabled
              />
            </div>
          ))}
        </div>
      </div>

      <div className='tw-flex tw-justify-end tw-w-full'>
        <Button
          variant='primary'
          buttonType='default'
          size='md'
          shape='square'
          disabled={!finalSlug}
          onClick={handleConfirm}
          className={finalSlug ? '!tw-bg-base-green' : ''}
        >
          Confirm
        </Button>
      </div>
    </>
  );
};

/**
 * Content of accordion step 2 - "Complaint Identification". Phase 1
 * supports exactly one complaint per order, covering every item
 * `identify_complaint` returned - the agent just picks the complaint type
 * from the options it resolved to and confirms.
 *
 * The intro text and uploaded-images strip don't depend on
 * `identify_complaint` at all, so they render immediately rather than
 * skeletoning along with the complaint-type section below.
 *
 * `products`/`images`/`complaintTypeOptions` default to mock data until a
 * live order is walked through - see constants.js.
 */
const ComplaintIdentification = ({
  products = MOCK_PRODUCTS,
  images = MOCK_UPLOADED_IMAGES,
  complaintTypeOptions = [],
  guidanceStatus = null,
  selectedParentSlug = null,
  onSelectedParentSlugChange = () => {},
  selectedSubSlug = null,
  onSelectedSubSlugChange = () => {},
  onConfirm = () => {},
  orderId = null,
  cartId = null,
  customerId = null,
}) => {
  return (
    <div className='tw-flex tw-flex-col tw-gap-[16px] tw-w-full tw-mt-4'>
      <p className='tw-text-[12px] tw-leading-tight tw-font-regular tw-text-grey-800 tw-m-0 tw-w-full'>
        {images.length === 0
          ? 'No images found. Request the customer to upload images to identify & select the complaint type for each affected product.'
          : 'Review the uploaded images and select the complaint type for the affected products as per the voice of the customer'}
      </p>

      <UploadedImages
        images={images}
        orderId={orderId}
        cartId={cartId}
        customerId={customerId}
      />

      <ComplaintTypeSection
        products={products}
        complaintTypeOptions={complaintTypeOptions}
        guidanceStatus={guidanceStatus}
        selectedParentSlug={selectedParentSlug}
        onSelectedParentSlugChange={onSelectedParentSlugChange}
        selectedSubSlug={selectedSubSlug}
        onSelectedSubSlugChange={onSelectedSubSlugChange}
        onConfirm={onConfirm}
      />
    </div>
  );
};

export default ComplaintIdentification;
